import { KeyRound } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, Field, Input } from '../../../components/ui';
import { useT } from '../../../i18n';
import { OTP_WATCH_MS, useOtpWatch } from './api';
import { normalizePhone } from './format';
import { Fail } from './shared';
import { OtpCode } from './StandActions';

/**
 * The OTP for any phone, without checking the account — for a manual test:
 * ask for a code in the app and it shows here (from Kafdrop, the last ten
 * minutes). For three minutes the tab asks again by itself. The phone goes to
 * TestRun in the request body only.
 */
export const OtpTab = () => {
  const { t } = useT();
  const [phone, setPhone] = useState('');
  const [asked, setAsked] = useState<{ phone: string; at: number } | null>(null);
  const [watching, setWatching] = useState(false);
  const otp = useOtpWatch(asked, watching);

  // three minutes after asking, the tab stops asking again
  useEffect(() => {
    if (!asked) return;
    const timer = setTimeout(() => setWatching(false), OTP_WATCH_MS);
    return () => clearTimeout(timer);
  }, [asked]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizePhone(phone);
    setPhone(normalized);
    setAsked({ phone: normalized, at: Date.now() });
    setWatching(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <form onSubmit={submit} className="space-y-3 px-4 py-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('automation.accounts.phone')} className="min-w-60 flex-1">
              {({ id }) => (
                <Input
                  id={id}
                  placeholder={t('automation.accounts.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => setPhone((p) => normalizePhone(p))}
                  inputMode="tel"
                  autoComplete="off"
                  required
                />
              )}
            </Field>
            <Button type="submit" variant="primary">
              <KeyRound className="size-4" aria-hidden="true" />
              {t('automation.accounts.otp.submit')}
            </Button>
          </div>
          <p className="text-[12px] text-ink-subtle">{t('automation.accounts.otp.hint')}</p>
        </form>
      </Card>
      {otp.isError ? <Fail title={t('automation.accounts.otp.failed')} error={otp.error} /> : null}
      {otp.data && asked ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <OtpCode result={otp.data} />
            <span className="text-[12px] text-ink-subtle">
              {asked.phone}
              {watching ? ` · ${t('automation.accounts.otp.watching')}` : ''}
              {otp.isFetching ? ` · ${t('automation.accounts.otp.asking')}` : ''}
            </span>
          </div>
        </Card>
      ) : null}
    </div>
  );
};
